import { useState } from 'react';
import api, { downloadFile } from '../api';

const DUPLICATE_MODES = [
  { value: 'skip', label: 'Skip Duplicates', hint: 'Leave existing matching records untouched (default)' },
  { value: 'update', label: 'Update Existing', hint: 'Overwrite matching records with the imported values' },
  { value: 'create_new', label: 'Import Anyway (as new)', hint: 'Add a new record even if a match is found' },
];

function ImportModal({ open, onClose, module, moduleLabel, companyId, onImported, supportsCreateNew = false }) {
  const [file, setFile] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const modes = supportsCreateNew ? DUPLICATE_MODES : DUPLICATE_MODES.filter((m) => m.value !== 'create_new');

  const handleDownloadTemplate = () => {
    downloadFile(`/import/template/${module}`, `Krosifin_${moduleLabel.replace(/\s+/g, '_')}_Template.xlsx`).catch(() =>
      setError('Could not download template')
    );
  };

  const handleFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
    setError(null);
  };

  const handleImport = () => {
    if (!file || !companyId) return;
    setUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_id', companyId);
    fd.append('duplicate_mode', duplicateMode);
    api
      .post(`/import/${module}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        setResult(res.data);
        setFile(null);
        if (res.data.success_count > 0 || res.data.updated_count > 0) onImported();
      })
      .catch((err) => setError(err.response?.data?.detail || 'Import failed'))
      .finally(() => setUploading(false));
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <div className="ncf-modal-overlay" onClick={handleClose}>
      <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ncf-modal-title">Import {moduleLabel}</h3>

        <p className="dash-table-sub" style={{ marginBottom: 12 }}>
          Download the template, fill it in, then upload it here. Rows with errors are skipped and
          listed below — valid rows still import.
        </p>

        <button
          type="button"
          className="dash-btn cal-btn-grey"
          onClick={handleDownloadTemplate}
          style={{ display: 'inline-block', marginBottom: 16 }}
        >
          Download Template
        </button>

        <div className="field-group">
          <span className="field-label">Excel or CSV File</span>
          <input type="file" accept=".xlsx,.xls,.csv" className="field-input" onChange={handleFile} />
        </div>

        <div className="field-group">
          <span className="field-label">If a matching record already exists</span>
          <div className="checkbox-list">
            {modes.map((m) => (
              <label key={m.value} className="checkbox-item" title={m.hint}>
                <input
                  type="radio"
                  name={`duplicate-mode-${module}`}
                  checked={duplicateMode === m.value}
                  onChange={() => setDuplicateMode(m.value)}
                />
                {m.label}
              </label>
            ))}
          </div>
        </div>

        {error && <p className="dash-error">{error}</p>}

        {result && (
          <div className="dash-card" style={{ marginTop: 12, padding: 12 }}>
            <p style={{ margin: 0 }}>
              <strong>{result.success_count}</strong> imported, <strong>{result.updated_count}</strong> updated,{' '}
              <strong>{result.duplicate_count}</strong> skipped as duplicate, <strong>{result.failure_count}</strong> failed.
            </p>
            {result.duplicates.length > 0 && (
              <>
                <p style={{ margin: '10px 0 4px', fontWeight: 600 }}>Skipped (duplicate)</p>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.duplicates.map((d, idx) => (
                      <tr key={idx}>
                        <td>{d.row}</td>
                        <td>{d.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {result.errors.length > 0 && (
              <>
                <p style={{ margin: '10px 0 4px', fontWeight: 600 }}>Failed</p>
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((e, idx) => (
                      <tr key={idx}>
                        <td>{e.row}</td>
                        <td>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        <div className="ncf-modal-actions">
          <button type="button" className="dash-btn cal-btn-grey" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="dash-btn dash-btn-orange"
            disabled={!file || uploading}
            onClick={handleImport}
          >
            {uploading ? 'Importing...' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportModal;
